// DevOps Pipeline Data v3 - Enhanced with fix data and scoring support

export const lanes = [
  { id: 'preflight', name: 'Pre-flight', color: '#888780', bgColor: 'rgba(136, 135, 128, 0.05)', style: 'dashed' },
  { id: 'aws', name: 'AWS infrastructure', color: '#E8812A', bgColor: 'rgba(232, 129, 42, 0.05)' },
  { id: 'app', name: 'App team', color: '#7F77DD', bgColor: 'rgba(127, 119, 221, 0.05)' },
  { id: 'gitlab', name: 'GitLab CI/CD', color: '#378ADD', bgColor: 'rgba(55, 138, 221, 0.05)' },
  { id: 'devops', name: 'DevOps governance', color: '#1A3A5C', bgColor: 'rgba(26, 58, 92, 0.05)' }
];

export const tiles = [
  // PRE-FLIGHT
  { 
    id: 'intake-form', 
    lane: 'preflight', 
    idx: 0, 
    title: 'Intake form', 
    sub: 'App team', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active',
    fix: { 
      broken: "No formal intake. Requests arrive via Teams/verbal. Key inputs missing: service list, versions, arch diagram, DNS, rollback plan.",
      ideal: "Teams-integrated form with mandatory fields. SLA: DevOps acknowledges within 1 business day. Nothing starts without it." 
    }
  },
  { 
    id: 'svc-version-matrix', 
    lane: 'preflight', 
    idx: 1, 
    title: 'Service & version matrix', 
    sub: 'App team', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "7-8 branches across repos. No source of truth. Wrong code deployed and hours wasted troubleshooting.",
      ideal: "3 protected branches: prod, UAT, dev. Auto-delete source branch on merge. Mandatory PR reviewers. Versioned matrix file in repo root." 
    }
  },
  { 
    id: 'arch-dep-doc', 
    lane: 'preflight', 
    idx: 2, 
    title: 'Arch + dependency doc', 
    sub: 'App team', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Hawk AI backend dependency on RAG branch was undocumented. Discovered only after deployment failed.",
      ideal: "Arch diagram + dependency map required in intake form. Updated via PR, never deleted." 
    }
  },
  { 
    id: 'tf-plan-review', 
    lane: 'preflight', 
    idx: 3, 
    title: 'Terraform plan review', 
    sub: 'DevOps + Architect', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active',
    fix: { 
      broken: "Review gate is missing entirely. No architect review before infra is built.",
      ideal: "After drafting Terraform plan, application architect reviews it. Confirms resource types, IAM roles, and environment sizing are correct before apply." 
    }
  },

  // AWS INFRASTRUCTURE
  { 
    id: 'eks-cluster', 
    lane: 'aws', 
    idx: 0, 
    title: 'EKS cluster', 
    sub: 'Base infrastructure', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'dimmed'
  },
  { 
    id: 'ecr-registry', 
    lane: 'aws', 
    idx: 1, 
    title: 'ECR registry', 
    sub: 'Lifecycle + scan-on-push', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active'
  },
  { 
    id: 'opensearch', 
    lane: 'aws', 
    idx: 2, 
    title: 'OpenSearch', 
    sub: 'Network + data access policy required', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'depends',
    fix: { 
      broken: "OpenSearch blocks all access without explicit network policy and data policy. Currently configured manually and inconsistently.",
      ideal: "Terraform module includes: aws_opensearchserverless_security_policy (network), aws_opensearchserverless_access_policy (data), IAM role binding for Bedrock agents." 
    }
  },
  { 
    id: 'sagemaker', 
    lane: 'aws', 
    idx: 3, 
    title: 'SageMaker endpoint', 
    sub: 'ML inference layer', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active',
    fix: { 
      broken: "Adam and Naveen spent an entire night manually creating SageMaker model+endpoint for noise reduction. Undocumented. Being repeated from scratch for sandbox.",
      ideal: "Terraform: aws_sagemaker_model + aws_sagemaker_endpoint_configuration + aws_sagemaker_endpoint. Endpoint name is a tf output flowing into values.yml." 
    }
  },
  { 
    id: 's3-iam', 
    lane: 'aws', 
    idx: 4, 
    title: 'S3 + IAM', 
    sub: 'Roles and buckets', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active'
  },
  { 
    id: 'dns-certs', 
    lane: 'aws', 
    idx: 5, 
    title: 'DNS + certs', 
    sub: 'Route53 + ACM', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active'
  },

  // APP TEAM
  { 
    id: 'local-docker', 
    lane: 'app', 
    idx: 0, 
    title: 'Local Docker test', 
    sub: 'Passes before push', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active'
  },
  { 
    id: 'push-gitlab', 
    lane: 'app', 
    idx: 1, 
    title: 'Push to GitLab', 
    sub: 'Triggers pipeline via webhook', 
    state: 'at-risk',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "No mandatory PR reviewers. Optional approvals. No auto-delete of source branches after merge. No branch protection rules enforced.",
      ideal: "Merge to protected branch (prod/UAT/dev) via PR. Mandatory reviewers. Source branch auto-deleted on merge. No direct pushes to protected branches." 
    }
  },

  // GITLAB CI/CD
  { 
    id: 'build-image', 
    lane: 'gitlab', 
    idx: 0, 
    title: 'Build image (once)', 
    sub: 'Immutable digest + SBOM', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Image rebuilt per environment. Same tag reused with different content. No immutable digest. Rollback requires knowing last-good hash which is unknown.",
      ideal: "GitLab CI builds one SHA-tagged image. Push to ECR with scan-on-push. Promote same SHA across envs. Rollback = helm rollback to previous release." 
    }
  },
  { 
    id: 'helm-deploy-1', 
    lane: 'gitlab', 
    idx: 1, 
    title: 'Helm deploy (initial)', 
    sub: 'First deploy — skeleton values', 
    state: 'at-risk',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    isChickenEgg: true
  },
  { 
    id: 'refine-values', 
    lane: 'gitlab', 
    idx: 2, 
    title: 'Refine values file', 
    sub: 'URLs now known — update', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active',
    isChickenEgg: true,
    fix: { 
      broken: "Currently done manually by DevOps — copy load balancer URLs, service endpoints, ECR URLs from console into values.yml. 2-4 hours per environment.",
      ideal: "CI runs: terraform output -json | scripts/gen-values.sh > values.${ENV}.yml. Auto-populates all endpoints. Zero manual editing." 
    }
  },
  { 
    id: 'helm-deploy-2', 
    lane: 'gitlab', 
    idx: 3, 
    title: 'Helm redeploy', 
    sub: 'With refined values', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'dimmed', 
    runtimeNew: 'active',
    isChickenEgg: true,
    fix: { 
      broken: "Second deploy currently triggered manually after values are hand-edited. No automated re-trigger after values refinement.",
      ideal: "gen-values.sh completion automatically triggers helm upgrade --install with refined values.yml. No manual trigger needed." 
    }
  },
  { 
    id: 'smoke-tests', 
    lane: 'gitlab', 
    idx: 4, 
    title: 'Smoke tests', 
    sub: 'Health + API paths + policy check', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Manual and inconsistent. No defined acceptance criteria. Coverage varies by engineer. Failures reported without diagnostics.",
      ideal: "Automated in CI: curl health endpoints, run python scripts/smoke_test.py, aws iam simulate-principal-policy. Pipeline posts pass/fail + log links." 
    }
  },
  { 
    id: 'svc-health-check', 
    lane: 'gitlab', 
    idx: 5, 
    title: 'Service health check', 
    sub: 'All services green before UAT', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "No automated service health check step. Health is inferred from smoke test results or manual inspection.",
      ideal: "Dedicated step: check all EKS pod readiness, SageMaker endpoint InService status, OpenSearch domain status. Must be green before use-case validation starts." 
    }
  },
  { 
    id: 'usecase-validation', 
    lane: 'gitlab', 
    idx: 6, 
    title: 'Use-case validation', 
    sub: 'Documented scenarios pass/fail', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Not automated and not documented for Nimbus Mind. Hawk AI has a documented 10-for-10 test but it is not formalized or version-controlled.",
      ideal: "Use-case test suite stored in repo. Each release runs the suite and posts score (e.g. 9/10). Gate: minimum passing score required before deploy-done." 
    }
  },
  { 
    id: 'deploy-done', 
    lane: 'gitlab', 
    idx: 7, 
    title: 'Deploy done', 
    sub: 'Tests pass = done', 
    state: 'healthy',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active'
  },

  // DEVOPS GOVERNANCE
  { 
    id: 'drift-detection', 
    lane: 'devops', 
    idx: 0, 
    title: 'Drift detection', 
    sub: 'Alerts on manual changes', 
    state: 'healthy',
    firstLaunch: 'dimmed', 
    runtimeCode: 'active', 
    runtimeNew: 'active'
  },
  { 
    id: 'iam-trimming', 
    lane: 'devops', 
    idx: 1, 
    title: 'IAM trimming', 
    sub: 'Incremental after app stable', 
    state: 'broken',
    firstLaunch: 'dimmed', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Not started. IAM permissions wide open. Adam's slim-down document exists but the exercise has not begun. No Access Analyzer in use.",
      ideal: "Naveen's transplant rule: full app works first, then trim one permission at a time using AWS Access Analyzer. Permission boundaries in Terraform from day 1." 
    }
  },
  { 
    id: 'rollback-ready', 
    lane: 'devops', 
    idx: 2, 
    title: 'Rollback ready', 
    sub: 'Digest + config recorded', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Rollback plan is verbal and untested. Previous working digest unknown. No defined rollback trigger or decision owner.",
      ideal: "Before every deployment: record previous image digest + config version. One-click rollback path in CI/CD. Named decision owner and trigger criteria in runbook." 
    }
  },
  { 
    id: 'monitoring', 
    lane: 'devops', 
    idx: 3, 
    title: 'Monitoring & alerting', 
    sub: 'CloudWatch + alarm profiles', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Missing entirely from current governance. Monitoring and alerting must be in place before going live, not added reactively.",
      ideal: "CloudWatch dashboard per service. Alarm profiles with SNS routing to DevOps on-call. SageMaker invocation errors, OpenSearch latency, EKS pod crashes all monitored." 
    }
  },
  { 
    id: 'runbooks', 
    lane: 'devops', 
    idx: 4, 
    title: 'Runbooks live', 
    sub: 'Stored in repo, linked from CI', 
    state: 'broken',
    firstLaunch: 'active', 
    runtimeCode: 'active', 
    runtimeNew: 'active',
    fix: { 
      broken: "Runbooks missing or stale. DevOps repeatedly explains same troubleshooting steps. Slow onboarding.",
      ideal: "Runbooks stored in repo, linked from pipeline output. Covers: deploy, rollback, scale, debug, rotate secrets. Updated via PR only." 
    }
  }
];

// Interview scenarios
export const interviewScenarios = [
  { id: 'scenario1', name: 'Full pipeline', description: 'All tiles in correct order' },
  { id: 'scenario2', name: 'Infra before app', description: 'AWS resources must be created before app deployment' },
  { id: 'scenario3', name: 'Values chicken-and-egg', description: 'Focus on the deploy → refine → redeploy cycle' },
  { id: 'scenario4', name: 'Runtime only', description: 'Code change deployment flow' },
  { id: 'scenario5', name: 'New service', description: 'Adding a new service to existing infrastructure' }
];

export const sampleYaml = `stages:
  - build
  - infra
  - configure
  - deploy
  - validate

variables:
  ENV: sandbox
  PROJECT: nimbusmind
  AWS_REGION: us-east-1

build-image:
  stage: build
  script:
    - docker build -t $ECR_URL/$PROJECT:$CI_COMMIT_SHA .
    - docker push $ECR_URL/$PROJECT:$CI_COMMIT_SHA
    - trivy image $ECR_URL/$PROJECT:$CI_COMMIT_SHA
  only: [main, develop]

terraform-plan:
  stage: infra
  script:
    - terraform init -backend-config=envs/$ENV/backend.conf
    - terraform plan -var-file=envs/$ENV.tfvars -out=tfplan
  artifacts: [tfplan]

terraform-apply:
  stage: infra
  script:
    - terraform apply tfplan
    - terraform output -json > tf_outputs.json
  when: manual

generate-values:
  stage: configure
  script:
    - ./scripts/gen-values.sh tf_outputs.json $ENV > values.$ENV.yml
  artifacts: [values.$ENV.yml]

helm-deploy:
  stage: deploy
  script:
    - helm upgrade --install $PROJECT ./chart \\
        --values values.$ENV.yml \\
        --set image.tag=$CI_COMMIT_SHA \\
        --wait --timeout 5m

smoke-tests:
  stage: validate
  script:
    - curl -f https://api.$ENV.$DOMAIN/health
    - python scripts/smoke_test.py --env $ENV
    - aws iam simulate-principal-policy ...`;
